import axios from "axios";
import MockAdapter from "axios-mock-adapter";

import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import i18n from "../i18n/config";
import { getAccessToken, clearStoredSession } from "../utils/session-user";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3333/api";
const api = axios.create({ baseURL });
api.interceptors.request.use(
    async function (config) {
        const accessToken = getAccessToken();
        if (accessToken) {
            config.headers["x-access-token"] = accessToken;
            config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    },
);

api.interceptors.response.use(
    function (response) {
        const isSignIn = response.config.url?.includes("/sign-in");
        // Background/silent calls (activity logging) must not surface a "created" toast.
        const isSilent = response.config.hideSuccessToast || response.config.url?.includes("/user/admin/activity");
        if (
            !isSignIn && !isSilent &&
            (response.status === 201 ||
            (response.status === 200 && response.config.method === "post"))
        ) {
            toast.success(i18n.t('common:api.created'));
        }
        if (!isSilent && response.status === 200 && response.config.method === "put") {
            toast.success(i18n.t('common:api.updated'));
        }
        return response;
    },
    function (error) {
        const response = error.response;
        if (!response) {
            // Network/timeout failure — no HTTP status to act on.
            Sentry.captureException(error);
            if (!error.config?.hideErrorToast) {
                toast.error(i18n.t('common:api.connectionError'));
            }
            return Promise.reject(error);
        }

        // Server-side failures are real incidents — report them.
        if (response.status >= 500) {
            Sentry.captureException(error);
        }
        
        if (error.config?.hideErrorToast) {
            return Promise.reject(error);
        }
        
        const errorMessage = response.data?.message || response.data?.error;

        if (response.status === 422) {
            const body: string[] = [];
            Object.values(response.data.body as Record<string, string>).forEach((fieldErrors) => {
                body.push(fieldErrors);
            });
            if (body.length) {
                toast.error(i18n.t('common:api.invalidData', { errors: body.join(", ") }));
            } else {
                toast.error(errorMessage || i18n.t('common:api.validationError'));
            }
        } else if (response.status === 401) {
            toast.error(i18n.t('common:api.unauthorized'));
            // A 401 on the sign-in request is just wrong credentials — don't wipe the session.
            // On any authenticated request it means the session expired/is invalid: drop it.
            const isSignInRequest = error.config?.url?.includes("/sign-in");
            if (!isSignInRequest) {
                clearStoredSession();
                if (window.location.pathname !== "/signin") {
                    window.location.assign("/signin");
                }
            }
        } else if (response.status === 403) {
            toast.error(i18n.t('common:api.forbidden'));
        } else if (response.status === 404) {
            toast.error(i18n.t('common:api.notFound'));
        } else if (response.status >= 500) {
            const serverStack = response.data?.stack || response.data?.error || response.data?.message;
            toast.error(i18n.t('common:api.serverError', { cause: serverStack || errorMessage }));
        } else {
            toast.error(errorMessage || i18n.t('common:api.unexpected', { status: response.status }));
        }

        return Promise.reject(error);
    },
);


export { api };
